import commonActions from '@/store/data/common/actions'
import { webRequestClient } from '@/services'
import { errorHandler, getTokenUserEmail } from '@/utils/helpers'
import helpers from './helpers'
import { isNil, get, toNumber, isEmpty, head } from 'lodash'
import Vue from 'vue'
import dayjs from 'dayjs'

const actions = {
    retrieveItems({ commit, dispatch, state, getters }) {
        commit('setIsLoading', true)
        const _route = helpers.buildRoutes(state)
        return webRequestClient
            .get(_route.route, _route.params)
            .then(accountData => {
                if (accountData && !isEmpty(accountData)) {
                    const _xDeviceConf = get(accountData, 'xdevice_conf_fields', {})
                    commit('setAccountLabel', { label: accountData.account_fields.label })
                    commit('setAccountCountryData', { items: accountData.country_fields })
                    commit('setAccountIsActive', accountData.account_fields.status === 'active')
                    commit('setProductAccountIsActive', accountData.product_account_fields.is_active === 'YES')
                    commit('setAccountLinkedAccountsDetails', { linkedAccounts: accountData.linked_accounts })
                    commit('setAccountIsRuleActivated', accountData.account_fields.rule_estimation_activated)
                    commit('setAccountIsEstimationFeatureAvailable', { isActivated: accountData.account_fields.rule_estimation_activated })
                    commit('setAccountXDeviceInfo', { xDeviceInfo: _xDeviceConf })
                    commit('setAccountIsBootstrapCustomSegmentAvailable', {
                        isActivated: get(accountData.account_fields, 'bootstrap_custom_segment_available', 'NO')
                    })
                    commit('setAccountIsWamFactoActivated', { isActivated: accountData.account_fields.wam_factory_activated })
                    commit('setAccountIsWamFactoLogsActivated', { isActivated: accountData.account_fields.wamfactory_logs_activated })
                    commit('setAccountAllocationDashboardActivated', accountData.account_fields.dashboard_alerts_activated)
                    commit('setWamFactoryRuleVersion', { version: accountData.account_fields.wamfactory_rules_version })
                    // dispatch('data/customTaxonomies/retrieveWamCustomTaxonomies', { accountId: state.accountId }, { root: true })
                    dispatch('ui/settings/setsUiWamFactoSettings', { accountData: accountData.account_fields }, { root: true })
                    dispatch('data/settings/setsAccountGeneralSettings', { accountData: accountData.account_fields, xdeviceData: _xDeviceConf }, { root: true })
                    dispatch('ui/settings/setsUiGeneralSettings', { accountData: accountData.account_fields, xdeviceData: _xDeviceConf }, { root: true })
                    dispatch('data/settings/setsAccountSocioDemoSettings', { accountData: accountData.account_fields }, { root: true })
                    dispatch('ui/settings/setsUiSocioDemoSettings', { accountData: accountData.account_fields }, { root: true })
                    dispatch('ui/settings/setsUiDataMiningSettings', { accountData: accountData.account_fields }, { root: true })
                    dispatch('notifications/retrieveInterfaceMessages', {}, { root: true })
                    dispatch('data/settings/setsAccountDataMiningSettings', { accountData: accountData.account_fields }, { root: true })
                    commit('setDailyBootstrapLimit', { limit: toNumber(accountData.account_fields.daily_bootstrap_limit) })
                    commit('setDailyPushOfStockLimit', { limit: toNumber(accountData.account_fields.daily_push_of_stock_limit) })
                } else {
                    const message = 'error during account settings retrieval'
                    throw Error(message)
                }
            })
            .then(() => {
                Promise.all([
                    ...getters.getDataTypes.reduce((acc, dataType) => {
                        if (dataType != 'georadar' && dataType != 'audience' && dataType != 'custom') {
                            return [...acc, dispatch('data/' + dataType + '/retrieveItems', {}, { root: true })]
                        }
                        return acc
                    }, []),
                    dispatch('data/audience/retrieveItems', {}, { root: true }),
                    dispatch('data/transfer/retrieveItems', {}, { root: true }),
                    dispatch('data/customTaxonomies/retrieveWamCustomTaxonomies', { accountId: state.accountId }, { root: true }),
                    dispatch('data/taxonomies/getSharedTaxonomies', null, { root: true }),
                    dispatch('data/georadar/retrieveGeoradarPoints', {}, { root: true }),
                    dispatch('data/settings/retrieveBusinessAppsProductAccount', {}, { root: true }),
                    dispatch('retrieveNamespaces')
                ])
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('setIsLoading', false)
            })
    },

    retrieveAccountId({ commit }, params) {
        return new Promise((resolve, reject) => {
            if (params && !isNil(params.accountId)) {
                resolve(commit('setAccountId', { id: params.accountId }))
            } else {
                var error = new Error('No user set')
                reject(error)
            }
        })
    },

    retrieveUserRights({ commit, state }) {
        commit('setIsLoading', true)
        const id = state.accountId
        const _params = {
            params: { email: encodeURI(getTokenUserEmail(Vue.prototype.$keycloak)) }
        }
        return webRequestClient
            .get(`/audience_admin/accounts/${id}/user_rights.json`, _params)
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while retrieving user rights', commit)
                    return 'failure'
                } else {
                    commit('setUserRights', { userRights: response })
                    return 'success'
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('setIsLoading', false)
            })
    },

    retrieveNamespaces({ commit }) {
        return webRequestClient
            .get('/audience_admin/namespaces.json?conditions={"status":[{"!=":"deleted"}]}')
            .then(response => {
                if (get(response, 'status', '') === 'KO') {
                    errorHandler('Error while retrieving account namespaces', commit)
                } else {
                    const _namespaceList = get(response, 'list', []).sort((a, b) => b.id - a.id)
                    if (!isEmpty(_namespaceList)) {
                        commit('setNamespaces', { namespaceList: _namespaceList })
                        commit('setDefaultNamespaceId')
                    }
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    createNamespace({ commit, state }, params) {
        return webRequestClient
            .post('/audience_admin/namespaces.json', { ...params, status: 'active', account_id: toNumber(state.accountId) })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while creating your namespace', commit)
                    return false
                } else {
                    commit('setNamespace', { namespace: response })
                    commit('notifications/setAddedNotification', { type: 'success', message: 'Namespace has been successfully created' }, { root: true })
                    commit('setDefaultNamespaceId', {})
                    return true
                }
            })
            .catch(error => {
                errorHandler(error, commit)
                return false
            })
    },

    editNamespace({ commit, state }, params) {
        const id = params.id
        delete params.id
        return webRequestClient
            .put(`/audience_admin/namespaces/${id}.json`, { ...params, account_id: toNumber(state.accountId) })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while editing namespace', commit)
                } else {
                    commit('setUpdatedNamespace', { namespace: response })
                    commit('notifications/setAddedNotification', { type: 'success', message: 'Namespace has been successfully updated' }, { root: true })
                    return true
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    deleteNamespace({ commit, state }, params) {
        return webRequestClient
            .delete(`/audience_admin/namespaces/${params.id}.json`, {
                data: { account_id: toNumber(state.accountId) }
            })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while deleting namespace', commit)
                } else {
                    commit('unsetNamespace', { namespace: response })
                    commit('notifications/setAddedNotification', { type: 'success', message: 'Namespace has been successfully deleted' }, { root: true })
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    retrieveAttributionCounterAudience({ commit, state }) {
        return webRequestClient
            .get('/audience_admin/attribution_counters.json', {
                params: {
                    conditions: {
                        account_id: state.accountId,
                        attribution_type: 'bootstrap',
                        day: dayjs().format('YYYY-MM-DD')
                    }
                }
            })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while retrieving attribution counter', commit)
                } else {
                    if (response.total_result !== '0') commit('setAttributionCounterAudience', { counter: toNumber(head(response.list).counter) })
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    retrieveAttributionCounterTransfer({ commit, state }) {
        return webRequestClient
            .get('/audience_admin/attribution_counters.json', {
                params: {
                    conditions: {
                        account_id: state.accountId,
                        attribution_type: 'push_of_stock',
                        day: dayjs().format('YYYY-MM-DD')
                    }
                }
            })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while retrieving attribution counter', commit)
                } else {
                    if (response.total_result !== '0') commit('setAttributionCounterTransfer', { counter: toNumber(head(response.list).counter) })
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    }
}

export default { ...commonActions, ...actions }
